import React, { useState } from 'react';
import {
  Save,
  Send,
  Users,
  Clock,
  Upload,
  X,
  Plus,
  FileText,
} from 'lucide-react';
import { useListTemplatesQuery } from '@/client/api/whatsapp/list-templates.query';
import { useBroadcastToContactsMutation } from '@/client/api/whatsapp/broadcast.mutation';
import { toast, Toaster } from 'sonner';
import { useListTagsQuery } from '@/client/api/whatsapp/list-tags.query';
import { useListNumbersQuery } from '@/client/api/whatsapp/list-numbers.query';
import { usePlaysQuery } from '@/client/api/marketing/plays';
import { isLinkedNumber, useSenderLabel } from './NumberBadge';
import { useCreateCampaignMutation } from '@/client/api/campaigns/lifecycle.mutation';
import { useNavigate } from 'react-router-dom';

interface Contact {
  id: string;
  name: string;
  phone: string;
}
interface Segment {
  id: string;
  name: string;
  count: number;
  contacts: Contact[];
}

function extractVariables(str: string) {
  const regex = /{{(.*?)}}/g;
  const matches = [];
  let match;

  while ((match = regex.exec(str)) !== null) {
    matches.push(match[1]);
  }

  return matches;
}


const NewCampaignPage: React.FC = () => {
  const { data, isLoading, error } = useListTemplatesQuery();
  const { data: numbersData, isLoading: numbersLoading, error: numbersError } = useListNumbersQuery();
  const { data: tagsData, isLoading: tagsLoading, error: tagsError } = useListTagsQuery();
  const templates = data?.data?.data || [];
  const tags = tagsData?.data?.tags || [];
  const navigate = useNavigate();
  const { mutateAsync: launchCampaign, isPending } = useBroadcastToContactsMutation();
  const { mutateAsync: createDraft, isPending: isSavingDraft } = useCreateCampaignMutation();

  const [campaignName, setCampaignName] = useState('');
  const [selectedSegments, setSelectedSegments] = useState<Segment | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [selectedNumber, setSelectedNumber] = useState('');
  const [playId, setPlayId] = useState('');
  const senderLabel = useSenderLabel();
  const { data: playsData } = usePlaysQuery({ limit: 200 });
  const [messageText, setMessageText] = useState('');
  const [scheduleType, setScheduleType] = useState<'now' | 'later'>('now');
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [description, setDescription] = useState('');
  const [showSegmentList, setShowSegmentList] = useState(false);
  const [attachments, setAttachments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<{ tag: string, contacts: number }[]>([]);
  const [variables, setVariables] = useState<string[]>([]);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [audienceType, setAudienceType] = useState<'segments' | 'csv'>('segments');

  // A linked phone (UltraMsg, an Evolution line) is not a Meta number: it has
  // no templates, so a campaign from it is free text.
  const isFreeText = isLinkedNumber(selectedNumber);

  const totalRecipients = selectedTags.map(t => t.contacts).reduce((acc, cur) => acc + cur, 0) || 0;

  const handleSaveDraft = async () => {
    if (!campaignName.trim()) {
      toast.error('Campaign name is required to save a draft');
      return;
    }
    try {
      // WhatsApp tag-based audience (selectedTags) has no equivalent in the
      // campaigns v2 audience model (segment_ids) — audience is finished in
      // the campaign editor after the draft is saved.
      const campaign = await createDraft({
        body: {
          name: campaignName,
          channels: ['whatsapp'],
          content: {
            body: description || undefined,
            whatsapp: selectedTemplate
              ? {
                  template_name: selectedTemplate,
                  language: templates.find(t => t.name === selectedTemplate)?.language || 'en_US',
                }
              : undefined,
          },
          audience: { segment_ids: [] },
          schedule: { mode: 'now' },
        },
      });

      toast.success('Draft saved — finish setting up your audience in the campaign editor');
      navigate(`/admin/relationship/campaigns/${campaign.campaign_id}/edit`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to save draft');
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments([...attachments, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(attachments.filter((_, i) => i !== index));
  };

  const handleTemplateChange = (template: string) => {
    setSelectedTemplate(template);
    const temp = templates.find(item => item.name === template)?.components.find(component => component.type === 'BODY')?.text || '';
    setVariables(extractVariables(temp));
  };

  const handleNumberChange = (alias: string) => {
    setSelectedNumber(alias);
  };

  const handleTagSelect = (tag: string, contacts: number) => {
    if (selectedTags.some(t => t.tag === tag)) {
      setSelectedTags(selectedTags.filter(t => t.tag !== tag));
      return
    }
    setSelectedTags([...selectedTags, { tag, contacts }]);
    setShowSegmentList(false);
  };

  const handleLaunchCampaign = async () => {
    try {
      const response = await launchCampaign({
        ...(isFreeText ? { body: messageText } : { template_name: selectedTemplate }),
        campaign: campaignName,
        params: isFreeText ? [] : variables,
        tags: selectedTags.map(t => t.tag),
        description: messageText,
        from_number: selectedNumber,
        ...(playId ? { play_id: Number(playId) } : {}),
        language_code: templates.find(item => item.name === selectedTemplate)?.language || 'en_US',
      });

      if (response.status !== 200) {
        throw new Error(response.data?.error_description || 'Error launching campaign. Please try again.');
      }

      toast.success('Campaign launched successfully!');
      navigate('/admin/relationship/whatsapp/broadcasts/campaigns', { replace: true });
    } catch (error) {
      toast.error((error as Error).message || 'Error launching campaign. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Create New Campaign</h1>
            <p className="text-sm text-gray-500 mt-1">Set up a new WhatsApp broadcast campaign</p>
          </div>
          <div className="flex items-center gap-2">
            {!isFreeText && <button
              onClick={handleSaveDraft}
              disabled={isSavingDraft}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50"
            >
              <Save className="w-4 h-4" />
              {isSavingDraft ? 'Saving...' : 'Save Draft'}
            </button>}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Campaign Details</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Campaign Name *
                  </label>
                  <input
                    type="text"
                    value={campaignName}
                    onChange={(e) => setCampaignName(e.target.value)}
                    placeholder="e.g., Summer Property Sale 2024"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description (Optional)
                  </label>
                  <textarea
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Brief description of this campaign..."
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Audience</h2>

              <div className="space-y-6">

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Segments *
                  </label>

                  <div className="relative">
                    <div className="flex items-center gap-2 flex-wrap p-3 border border-gray-300 rounded-lg min-h-[48px]">
                      {selectedTags.map(({ tag, contacts }) => (
                        <div
                          key={tag}
                          className="flex items-center gap-2 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                        >
                          <span>{tag} ({contacts.toLocaleString()})</span>
                          <button
                            onClick={() => handleTagSelect(tag, contacts)}
                            disabled={contacts === 0}
                            className="hover:bg-blue-200 rounded-full p-0.5"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => setShowSegmentList(!showSegmentList)}
                        className="flex items-center gap-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900"
                      >
                        <Plus className="w-4 h-4" />
                        Add segment
                      </button>
                    </div>

                    {showSegmentList && (
                      <div className="absolute z-10 mt-2 w-full bg-white border border-gray-300 rounded-lg shadow-lg">
                        <div className="p-3 border-b">
                          <p className="text-sm font-medium text-gray-700 mb-2">Available Segments</p>
                        </div>
                        <div className="max-h-64 overflow-y-auto">
                          {tags.map(({ tag, contacts }) => (
                            <button
                              key={tag}
                              onClick={() => handleTagSelect(tag, contacts)}
                              className="w-full p-3 hover:bg-gray-50 text-left flex items-center justify-between border-b last:border-b-0 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <div>
                                <p className="font-medium text-gray-900">{tag}</p>
                                <p className="text-sm text-gray-500">{contacts.toLocaleString()} contacts</p>
                              </div>
                              {selectedTags.includes({ tag, contacts }) && (
                                <span className="text-xs text-green-600 font-medium">Selected</span>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {selectedSegments && (
                    <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm text-blue-700">
                        <Users className="w-4 h-4 inline mr-1" />
                        Total Recipients: <strong>{totalRecipients.toLocaleString()}</strong>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Message Content</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marketing campaign (optional)
                  </label>
                  <select
                    value={playId}
                    onChange={(e) => setPlayId(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Not part of a marketing campaign</option>
                    {playsData?.items?.map(play => (
                      <option key={play.play_id} value={play.play_id}>{play.name}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-2">
                    Sent and reply counts from this broadcast will show on that campaign's results.
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select From Number *
                  </label>
                  <select
                    value={selectedNumber}
                    onChange={(e) => handleNumberChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a number...</option>
                    {numbersData?.data?.map(number => (
                      <option key={number.phone_number_id} value={number.alias || number.phone_number_id}>
                        {senderLabel(number.phone_number_id)?.text}
                      </option>
                    ))}
                  </select>
                </div>
                {!isFreeText && <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Use Template *
                  </label>
                  <select
                    value={selectedTemplate}
                    onChange={(e) => handleTemplateChange(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select a template...</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.name} className=' uppercase'>
                        <span className=' uppercase'>{template.name.replace('_', ' ').toUpperCase()}</span> - {template.category}
                      </option>
                    ))}
                  </select>
                  {variables && variables.length > 0 && (
                    <div className="mt-2">
                      <p className="text-xs text-gray-500">
                        Available variables: {variables.map(v => `{{${v}}}`).join(', ')}
                      </p>
                    </div>
                  )}
                </div>}

                {isFreeText && <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Message Text *
                  </label>
                  <textarea
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    placeholder="Type your message here... {{name}} is replaced with each contact's name."
                    rows={8}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none font-mono text-sm"
                  />
                  <div className="flex items-center justify-between mt-2">
                    <p className="text-xs text-gray-500">
                      Sent as plain text from the linked line, one message at a time.
                      Any contact field works as a variable: {'{{name}}'}, {'{{phone}}'}.
                    </p>
                    <p className="text-sm text-gray-500">{messageText.length} characters</p>
                  </div>
                </div>}

              </div>
            </div>

          </div>

          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Campaign Summary</h3>

              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Campaign Name</p>
                  <p className="text-sm font-medium text-gray-900">
                    {campaignName || 'Not set'}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-1">Total Recipients</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {totalRecipients.toLocaleString()}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-1">Sending From</p>
                  <p className="text-sm font-medium text-gray-900">
                    {selectedNumber ? senderLabel(selectedNumber)?.text : 'Not set'}
                  </p>
                </div>

                <div className="border-t pt-4">
                  <p className="text-sm text-gray-600 mb-1">Message Length</p>
                  <p className="text-sm font-medium text-gray-900">
                    {messageText.length} characters
                  </p>
                </div>

                <div className="border-t pt-4 space-y-2">
                  <button
                    onClick={handleLaunchCampaign}
                    disabled={
                      !campaignName ||
                      (isFreeText
                        ? !messageText.trim()
                        : !selectedTemplate || variables.length === 0) ||
                      !selectedNumber ||
                      selectedTags.length === 0
                    }
                    className="w-full px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    Launch Campaign
                  </button>

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Toaster position='top-right' duration={5000} richColors={true} />
    </div>
  );
};

export default NewCampaignPage;
